import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Gift, Copy, Users, CheckCircle2, Clock, Coins } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className={`inline-flex items-center justify-center h-10 w-10 rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-3xl font-display font-bold mt-3 tracking-tight tabular-nums">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referral").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const copy = () => {
    if (data?.link) {
      navigator.clipboard.writeText(data.link);
      toast.success("Invite link copied");
    }
  };

  const invitees = data?.invitees || [];
  const capReached = data && data.count >= data.cap;

  return (
    <div className="min-h-screen grain flex flex-col">
      <Navbar />
      <main data-testid="referrals-page" className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-indigo-100 text-indigo-600">
            <Gift className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Refer &amp; earn</h1>
            <p className="text-slate-500 text-sm">
              Earn <strong>{data?.reward ?? 5} credits</strong> each time a friend makes their first purchase — up to {data?.cap ?? 20} friends.
            </p>
          </div>
        </div>

        {/* Invite link */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6">
          <label className="text-sm font-medium text-slate-700">Your invite link</label>
          <div className="mt-2 flex items-center gap-2">
            <input
              data-testid="referral-link"
              readOnly
              value={data?.link || "Generating your link…"}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono text-slate-700 truncate"
            />
            <Button
              data-testid="referral-copy"
              onClick={copy}
              disabled={!data?.link}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
            >
              <Copy className="h-4 w-4 mr-1.5" /> Copy
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <Stat testid="stat-invited" icon={Users} label="Friends invited" value={data?.invited ?? 0} accent="bg-slate-100 text-slate-600" />
          <Stat testid="stat-converted" icon={CheckCircle2} label="Converted" value={data?.converted ?? 0} accent="bg-emerald-100 text-emerald-600" />
          <Stat testid="stat-pending" icon={Clock} label="Joined, not yet bought" value={data?.pending ?? 0} accent="bg-amber-100 text-amber-600" />
          <Stat testid="stat-earned" icon={Coins} label="Credits earned" value={data?.earned ?? 0} accent="bg-indigo-100 text-indigo-600" />
        </div>

        {capReached && (
          <div data-testid="referral-cap-banner" className="mt-4 rounded-xl bg-indigo-50 border border-indigo-200 p-4 text-sm text-indigo-800">
            🎉 You've hit the {data.cap}-referral reward cap — thank you for spreading the word! New friends can still join, but no further credits will be granted.
          </div>
        )}

        {/* Invitee list */}
        <div className="bg-white border border-slate-200 rounded-2xl mt-6 overflow-hidden" data-testid="invitee-list">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-display text-lg font-semibold">Your invitees</h2>
            <p className="text-xs text-slate-500">Identities are masked for your friends' privacy.</p>
          </div>

          {loading ? (
            <div className="px-5 py-10 text-center text-slate-400 text-sm">Loading…</div>
          ) : invitees.length === 0 ? (
            <div data-testid="invitee-empty" className="px-5 py-12 text-center">
              <Users className="h-8 w-8 text-slate-300 mx-auto" />
              <p className="text-slate-500 text-sm mt-3">No one has joined with your link yet.</p>
              <p className="text-slate-400 text-xs mt-1">Share your invite link above to start earning credits.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {invitees.map((inv, i) => (
                <li key={i} data-testid={`invitee-row-${i}`} className="px-5 py-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-semibold shrink-0">
                      {(inv.first_name || inv.email_masked || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{inv.first_name || "Friend"}</p>
                      <p className="text-xs text-slate-400 font-mono truncate">{inv.email_masked}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs text-slate-400 hidden sm:block">Joined {fmtDate(inv.joined_at)}</span>
                    {inv.converted ? (
                      <span data-testid={`invitee-status-${i}`} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Converted
                      </span>
                    ) : (
                      <span data-testid={`invitee-status-${i}`} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        <Clock className="h-3.5 w-3.5" /> Pending
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
